import { Component, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { StoriesService } from '../../features/stories/stories.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { NoDataComponent } from '../../shared/components/no-data/nodata.component';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';

@Component({
  selector: 'app-video-list',
  imports: [
    CommonModule,
    NoDataComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    PermissionDirective,
    ContentLoaderComponent,
  ],
  templateUrl: './video-list.component.html',
  styleUrl: './video-list.component.scss',
})
export class VideoListComponent {
  readonly dialog = inject(MatDialog);
  public storyList: any[] = [];
  public languages: any[] = [];
  public langCode: String = '';
  private searchSubject = new Subject<string>();
  noData = false;
  loading = false;
  searchTerm: string = '';

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private storiesService: StoriesService,
    private tagsService: TagsLanguageService
  ) {}

  ngOnInit() {
    this.getAllVideoStory(this.langCode);
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllVideoStory(this.langCode, searchTerm);
      });
  }

  onAddVideoStory() {
    this.router.navigate(['video/new']);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/placeHolderImage.jpg';
  }

  openDialog(content: any): void {
    const dialogRef = this.dialog.open(VideoViewModal, {
      panelClass: 'custom-story-panel',
      data: content,
    });
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onLanguageSelect(event: any) {
    this.langCode = event.value;
    this.getAllVideoStory(this.langCode);
  }

  deleteVideoStory(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.storiesService.removeStory(id).subscribe({
          next: (res) => {
            this.toastr.success(res?.message);
            this.getAllVideoStory(this.langCode);
          },
          error: (err) => {
            this.toastr.error('Failed to delete story');
          },
        });
      }
    });
  }

  editVideoStory(id: any) {
    this.router.navigate([`video/edit/${id}`]);
  }

  getAllVideoStory(lang: any, search = ''): void {
    this.loading = true;
    this.storiesService.getVideoStories(lang, search).subscribe({
      next: (res) => {
        this.storyList = res?.data;
        if (this.storyList.length === 0) {
          this.noData = true;
        } else {
          this.noData = false;
        }
        this.loading = false;
      },
      error: (err) => {
        console.log(err);
        this.loading = false;
      },
    });
  }

  getTranslationName(story: any): string {
    if (!story?.translations?.length) return 'Untitled';
    const preferred = story.translations.find((t: any) => t.langId === 1);
    return preferred ? preferred.name : story.translations[0].name;
  }
}

@Component({
  selector: 'video-view-modal',
  templateUrl: './video-view-modal/video-view.component.html',
  styleUrl: './video-view-modal/video-view.component.scss',
  imports: [MatDialogContent, CommonModule],
})
export class VideoViewModal implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<VideoViewModal>);
  private sanitizer = inject(DomSanitizer);
  private loadTimeout: any;
  loadError = false;
  safeYouTubeUrl: SafeResourceUrl | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}

  ngOnInit() {
    if (this.isYouTubeVideo(this.data)) {
      const videoId = this.extractYouTubeId(this.data);
      if (videoId) {
        this.safeYouTubeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`
        );
      }
    }
    // Start 1-minute timer
    this.loadTimeout = setTimeout(() => {
      this.loadError = true;
    }, 60000);
  }

  ngOnDestroy() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  isYouTubeVideo(url: string): boolean {
    return url?.includes('youtube.com') || url?.includes('youtu.be');
  }

  private extractYouTubeId(url: string): string | null {
    const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/;
    const match = url?.match(regExp);
    return match ? match[1] : null;
  }

  onVideoLoaded() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
    this.loadError = false;
  }

  onVideoError() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
    this.loadError = true;
  }
}
